import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Overline } from "@/components/brand/SectionDivider";
import { BRAND } from "@/data/brand";
import { submitContact } from "@/lib/api";
import { Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";
import { AmbientLight } from "@/components/brand/AmbientLight";
import useDocumentTitle from "@/lib/useDocumentTitle";

export default function Contact() {
  useDocumentTitle("Contact | AG Public Adjusting");
  const [data, setData] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Inject LocalBusiness JSON-LD via a script node with .textContent — safe (no HTML parsing, no XSS surface).
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "AG Public Adjusting",
      image: BRAND.logoWhite,
      telephone: BRAND.phoneDisplay,
      email: BRAND.email,
      url: window.location.origin,
      address: {
        "@type": "PostalAddress",
        streetAddress: BRAND.address.street,
        addressLocality: BRAND.address.city,
        addressRegion: BRAND.address.state,
        postalCode: BRAND.address.zip,
        addressCountry: BRAND.address.country,
      },
      openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Saturday", "Sunday"], opens: "00:00", closes: "00:00", description: "By Appointment Only" },
      ],
      description:
        "Licensed Florida public adjusting firm representing policyholders exclusively in hurricane, fire, water, and complex property insurance claims.",
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.message) { toast.error("Please fill in name, email, and message."); return; }
    setSubmitting(true);
    try {
      await submitContact(data);
      setDone(true);
      toast.success("Message received. The firm will respond shortly.");
    } catch { toast.error("Unable to send. Please try again or call us directly."); }
    finally { setSubmitting(false); }
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-20 relative bg-midnight-gradient overflow-hidden">
      <AmbientLight size={1100} color="blue" intensity={0.4} style={{ top: "10%", right: "0%" }} />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <Overline className="mb-8">Contact</Overline>
        <h1 className="font-display-tight text-white text-[clamp(2.8rem,6vw,6rem)] leading-[0.95] tracking-[-0.045em] max-w-3xl">
          Reach the firm
          <br />
          <span className="gold-metallic font-display font-light">directly.</span>
        </h1>

        <div className="mt-20 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 space-y-10">
            <a href={BRAND.phoneHref} className="flex items-start gap-5 text-white hover:text-gold transition-colors group" data-testid="contact-phone">
              <div className="w-12 h-12 rounded-full border border-gold/30 group-hover:border-gold flex items-center justify-center transition-colors">
                <Phone className="w-4 h-4 text-gold" strokeWidth={1.4} />
              </div>
              <div>
                <Overline className="text-[0.62rem] mb-2 text-ag-text-muted">Direct Line</Overline>
                <div className="font-display text-3xl tracking-[-0.025em] font-light">{BRAND.phoneDisplay}</div>
              </div>
            </a>

            <a href={BRAND.emailHref} className="flex items-start gap-5 text-white hover:text-gold transition-colors group" data-testid="contact-email">
              <div className="w-12 h-12 rounded-full border border-gold/30 group-hover:border-gold flex items-center justify-center transition-colors">
                <Mail className="w-4 h-4 text-gold" strokeWidth={1.4} />
              </div>
              <div>
                <Overline className="text-[0.62rem] mb-2 text-ag-text-muted">Email</Overline>
                <div className="font-display text-2xl tracking-[-0.025em] font-light">{BRAND.email}</div>
              </div>
            </a>

            <a href={BRAND.address.mapsHref} target="_blank" rel="noopener noreferrer" className="flex items-start gap-5 text-white hover:text-gold transition-colors group" data-testid="contact-address">
              <div className="w-12 h-12 rounded-full border border-gold/30 group-hover:border-gold flex items-center justify-center transition-colors">
                <MapPin className="w-4 h-4 text-gold" strokeWidth={1.4} />
              </div>
              <div>
                <Overline className="text-[0.62rem] mb-2 text-ag-text-muted">Office</Overline>
                <div className="font-display text-2xl text-white tracking-[-0.025em] font-light leading-[1.25]">
                  {BRAND.address.street}<br />
                  {BRAND.address.city}, {BRAND.address.state} {BRAND.address.zip}
                </div>
                <div className="font-mono text-[0.6rem] text-ag-text-muted mt-3 tracking-[0.22em] uppercase">{BRAND.licenseNumber}</div>
              </div>
            </a>

            <div className="flex items-start gap-5" data-testid="contact-hours">
              <div className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-white/70">
                <Clock className="w-4 h-4" strokeWidth={1.4} />
              </div>
              <div>
                <Overline className="text-[0.62rem] mb-2 text-ag-text-muted">Office Hours</Overline>
                <div className="space-y-1.5">
                  {BRAND.hours.map((h) => (
                    <div key={h.day} className="flex items-baseline gap-4 text-sm font-light">
                      <span className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-gold/85 min-w-[10rem]">{h.day}</span>
                      <span className="text-white/85">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="glass-strong p-10 text-center" data-testid="contact-success">
                <h3 className="font-display text-3xl text-white mb-4 font-light tracking-[-0.025em]">Message received.</h3>
                <p className="text-ag-text-secondary font-light leading-[1.65]">
                  The firm will respond to your inquiry shortly. For urgent matters, please call directly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="glass-strong p-8 lg:p-12" data-testid="contact-form">
                <h2 className="font-display text-3xl text-white mb-10 font-light tracking-[-0.025em]">Send a message</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="FULL NAME *">
                    <input value={data.full_name} onChange={(e) => update("full_name", e.target.value)} className="ag-input" required data-testid="contact-name" />
                  </Field>
                  <Field label="EMAIL *">
                    <input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} className="ag-input" required data-testid="contact-input-email" />
                  </Field>
                  <Field label="PHONE">
                    <input type="tel" value={data.phone} onChange={(e) => update("phone", e.target.value)} className="ag-input" data-testid="contact-input-phone" />
                  </Field>
                  <Field label="SUBJECT">
                    <input value={data.subject} onChange={(e) => update("subject", e.target.value)} className="ag-input" data-testid="contact-subject" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="MESSAGE *">
                      <textarea rows={5} value={data.message} onChange={(e) => update("message", e.target.value)} className="ag-input resize-none" required data-testid="contact-message" />
                    </Field>
                  </div>
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="mt-10 btn-gold inline-flex items-center gap-3 disabled:opacity-50">
                  {submitting ? "Sending…" : "Send Message"}
                  <ArrowRight className="w-3 h-3" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Google Map — 30 Broadway, Kissimmee, FL 34741                */}
      {/* ============================================================ */}
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 mt-24" data-testid="contact-map-wrap">
        <Overline className="mb-6">Office Location</Overline>
        <div className="relative overflow-hidden border border-white/10 rounded-sm" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px rgba(0,170,255,0.06)" }}>
          <iframe
            title="AG Public Adjusting — 30 Broadway, Kissimmee, FL 34741"
            src="https://www.google.com/maps?q=30+Broadway,+Kissimmee,+FL+34741&hl=en&z=16&output=embed"
            width="100%"
            height="460"
            style={{ border: 0, filter: "grayscale(0.4) contrast(0.92) brightness(0.85)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
          {/* subtle gold border accent */}
          <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 0 1px rgba(198,168,107,0.12)" }} />
        </div>
        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="font-mono text-[0.65rem] tracking-[0.22em] uppercase text-ag-text-muted">
            {BRAND.address.full}
          </span>
          <a
            href={BRAND.address.mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-map-open"
            className="font-mono text-[0.65rem] tracking-[0.22em] uppercase text-gold hover:text-gold-light inline-flex items-center gap-2"
          >
            Open in Google Maps <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </main>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">{label}</span>
    {children}
  </label>
);
